import React, { useState } from 'react';
import { Card } from '../components/Common';

const SECTIONS = [
  { id: 'applications', label: '1. Building an Application' },
  { id: 'engagements', label: '2. Creating an Engagement' },
  { id: 'management', label: '3. Engagement Management' },
  { id: 'findings', label: '4. Adding Vulnerabilities' },
  { id: 'workflow', label: '5. Navigating the Workflow' },
  { id: 'reports', label: '6. Generating Reports' },
];

const s = {
  h2: { fontSize: 20, fontWeight: 700, margin: '32px 0 12px', color: 'var(--text)', borderBottom: '1px solid var(--border)', paddingBottom: 8 },
  h3: { fontSize: 16, fontWeight: 600, margin: '20px 0 8px', color: 'var(--text)' },
  p: { fontSize: 14, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 12px' },
  ul: { fontSize: 14, lineHeight: 1.8, color: 'var(--text-secondary)', margin: '0 0 12px', paddingLeft: 20 },
  ol: { fontSize: 14, lineHeight: 1.8, color: 'var(--text-secondary)', margin: '0 0 12px', paddingLeft: 20 },
  tip: { padding: '10px 14px', borderRadius: 8, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', fontSize: 13, color: 'var(--text)', marginBottom: 16 },
  warn: { padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', fontSize: 13, color: 'var(--text)', marginBottom: 16 },
  badge: (color) => ({ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: color + '18', color, marginRight: 4 }),
  field: { display: 'inline-code', fontFamily: 'monospace', fontSize: 12, padding: '1px 6px', borderRadius: 4, background: 'var(--bg-input)', color: 'var(--primary)', border: '1px solid var(--border)' },
};

export default function Help() {
  const [activeSection, setActiveSection] = useState('applications');

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Help Center</h1>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }}>How to set up and manage penetration testing engagements in AttackPath</p>

      <div style={{ display: 'flex', gap: 24 }}>
        {/* Left Nav */}
        <div style={{ width: 220, flexShrink: 0, position: 'sticky', top: 68, alignSelf: 'flex-start' }}>
          <Card style={{ padding: 8 }}>
            {SECTIONS.map(sec => (
              <button key={sec.id} onClick={() => { setActiveSection(sec.id); document.getElementById(sec.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                style={{
                  display: 'block', width: '100%', padding: '8px 12px', borderRadius: 6, border: 'none',
                  background: activeSection === sec.id ? 'var(--primary-light, rgba(59,130,246,0.1))' : 'transparent',
                  color: activeSection === sec.id ? 'var(--primary)' : 'var(--text-secondary)',
                  fontSize: 13, fontWeight: activeSection === sec.id ? 600 : 400, textAlign: 'left',
                  cursor: 'pointer', fontFamily: 'inherit', marginBottom: 2,
                }}>
                {sec.label}
              </button>
            ))}
          </Card>
        </div>

        {/* Content */}
        <div style={{ flex: 1, maxWidth: 780 }}>
          {/* Section 1: Applications */}
          <Card id="applications" style={{ padding: 24, marginBottom: 24 }}>
            <h2 style={s.h2}>1. Building an Application</h2>
            <p style={s.p}>
              Applications represent the systems your organization owns and tests. Every penetration test engagement should be tied to an application so testing history, scope, and compliance are tracked in one place.
            </p>

            <h3 style={s.h3}>Creating a New Application</h3>
            <ol style={s.ol}>
              <li>Navigate to <strong>Applications</strong> in the sidebar</li>
              <li>Click <strong>+ New Application</strong> in the top right</li>
              <li>Fill in the required fields:
                <ul style={{ ...s.ul, marginTop: 4 }}>
                  <li><strong>Client</strong> — Select the client that owns this application (MSP users only; auto-set for client users)</li>
                  <li><strong>Application Name</strong> — A descriptive name (e.g., "Customer Portal", "Payment API")</li>
                </ul>
              </li>
              <li>Set the application properties:
                <ul style={{ ...s.ul, marginTop: 4 }}>
                  <li><strong>Customer Tracking ID</strong> — Your internal reference number</li>
                  <li><strong>PCI Required</strong> — Whether PCI DSS compliance testing is needed</li>
                  <li><strong>Internet Facing</strong> — Whether the application is accessible from the internet</li>
                  <li><strong>Company Owned / Company Hosted</strong> — Ownership and hosting details</li>
                  <li><strong>Payment / Checkout</strong> — Whether the app handles payments</li>
                  <li><strong>IP Whitelist Needed</strong> — Whether testing IPs need to be whitelisted</li>
                </ul>
              </li>
              <li>Click <strong>Save</strong></li>
            </ol>

            <h3 style={s.h3}>Adding Scope Items</h3>
            <p style={s.p}>Scope items define the specific targets to be tested for this application.</p>
            <ol style={s.ol}>
              <li>In the application create/edit form, scroll to the <strong>Scope Items</strong> section</li>
              <li>Select a <strong>Type</strong>: URL, IP Address, API Endpoint, CIDR Range, Domain, or Mobile App</li>
              <li>Enter the <strong>Value</strong> (e.g., <code style={s.field}>https://portal.acme.com</code>)</li>
              <li>Optionally add a <strong>Description</strong></li>
              <li>Click <strong>Add</strong> — the scope item appears in the list below</li>
            </ol>

            <div style={s.tip}>
              <strong>Tip:</strong> When you create an engagement linked to this application, all scope items are automatically copied to the engagement. You can also import applications in bulk using the <strong>Import CSV</strong> feature.
            </div>
          </Card>

          {/* Section 2: Engagements */}
          <Card id="engagements" style={{ padding: 24, marginBottom: 24 }}>
            <h2 style={s.h2}>2. Creating an Engagement</h2>
            <p style={s.p}>
              An engagement represents a single penetration test — a time-bounded assessment of a specific application or network.
            </p>

            <h3 style={s.h3}>Creating a New Engagement</h3>
            <ol style={s.ol}>
              <li>Navigate to <strong>Engagements</strong> in the sidebar</li>
              <li>Click <strong>+ New Engagement</strong> in the top right</li>
              <li>Fill in the required fields:
                <ul style={{ ...s.ul, marginTop: 4 }}>
                  <li><strong>Client</strong> — Select the client (MSP users)</li>
                  <li><strong>Engagement Type</strong> — Choose from:
                    <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <span style={s.badge('#3b82f6')}>Network Penetration Test</span>
                      <span style={s.badge('#8b5cf6')}>Application Penetration Test</span>
                      <span style={s.badge('#06b6d4')}>Mobile Application Test</span>
                      <span style={s.badge('#ef4444')}>Red Team</span>
                      <span style={s.badge('#f59e0b')}>API Penetration Test</span>
                    </div>
                  </li>
                  <li><strong>Application</strong> — Link to an existing application (auto-populates scope, tracking ID, and properties)</li>
                </ul>
              </li>
              <li>Set optional details: Environment Type, CREST Required, Third Party vendor</li>
              <li>Click <strong>Create</strong></li>
            </ol>

            <div style={s.tip}>
              <strong>Shortcut:</strong> You can also create an engagement directly from the Applications page by clicking the <strong>+ Test</strong> button on any application row. This pre-fills the client and application fields.
            </div>

            <h3 style={s.h3}>Inline Editing on the Engagements List</h3>
            <p style={s.p}>Many fields can be edited directly from the engagements list without opening the detail page:</p>
            <ul style={s.ul}>
              <li><strong>Status</strong> — Click the status pill to change it via dropdown</li>
              <li><strong>Tester</strong> — Click the tester avatar/name to reassign</li>
              <li><strong>Start / End Dates</strong> — Click the date pill to open a date picker</li>
              <li><strong>SoW</strong> — Assign a Statement of Work from the dropdown</li>
              <li><strong>Third Party</strong> — Toggle the checkbox</li>
            </ul>
          </Card>

          {/* Section 3: Engagement Management */}
          <Card id="management" style={{ padding: 24, marginBottom: 24 }}>
            <h2 style={s.h2}>3. Engagement Management</h2>

            <h3 style={s.h3}>Engagement Detail Tabs</h3>
            <p style={s.p}>Click an engagement to open its detail page. The page is organized into tabs:</p>
            <ul style={s.ul}>
              <li><strong>Overview</strong> — All engagement metadata, application scoping details, testing restrictions, and action buttons</li>
              <li><strong>Findings</strong> — Vulnerability list with add, edit, delete, and import capabilities</li>
              <li><strong>Scope</strong> — Target scope items and encrypted credentials for testing</li>
              <li><strong>Reports</strong> — Generated reports with download, delivery tracking, and certificate management</li>
              <li><strong>Documents</strong> — Uploaded files: scope documents, NDA, rules of engagement, API collections, etc.</li>
              <li><strong>Notes</strong> — Timestamped team notes</li>
              <li><strong>Activity</strong> — Audit trail of all changes to this engagement</li>
            </ul>

            <h3 style={s.h3}>Adding Engagement Details</h3>
            <p style={s.p}>Click <strong>Edit</strong> in the engagement header to open the full edit form. Key fields include:</p>
            <ul style={s.ul}>
              <li><strong>Assigned Tester</strong> — Who will perform the test</li>
              <li><strong>Start / End Dates</strong> — The testing window (required before moving to In Review)</li>
              <li><strong>Technical Manager</strong> — The person responsible for sign-off</li>
              <li><strong>Customer Tracking ID / Service Order</strong> — Client reference numbers</li>
              <li><strong>Testing Restrictions</strong> — Per-day time windows with timezone</li>
              <li><strong>Scoping Comments / Challenges</strong> — Notes from the scoping phase</li>
            </ul>

            <h3 style={s.h3}>Uploading Documents</h3>
            <p style={s.p}>Go to the <strong>Documents</strong> tab to upload supporting files:</p>
            <ol style={s.ol}>
              <li>Select a category from the dropdown: General, Scope Document, Rules of Engagement, NDA, Authorization, Network Diagram, API Collection, Other</li>
              <li>Click <strong>Upload</strong> and select one or more files</li>
              <li>Files appear in the list with category badge, size, uploader, and date</li>
            </ol>

            <h3 style={s.h3}>Managing Credentials</h3>
            <p style={s.p}>The <strong>Scope</strong> tab includes an encrypted credentials section. Add test credentials (Basic Auth, Bearer Token, API Key, Cookie, Form Login) that are stored encrypted and visible only to the assigned tester and admins.</p>
          </Card>

          {/* Section 4: Findings */}
          <Card id="findings" style={{ padding: 24, marginBottom: 24 }}>
            <h2 style={s.h2}>4. Adding Vulnerabilities</h2>
            <p style={s.p}>Findings (vulnerabilities) are the core deliverable of a penetration test. There are three ways to add them.</p>

            <h3 style={s.h3}>Manual Creation</h3>
            <ol style={s.ol}>
              <li>Open an engagement and go to the <strong>Findings</strong> tab</li>
              <li>Click <strong>+ Add Finding</strong></li>
              <li>Fill in the finding details:
                <ul style={{ ...s.ul, marginTop: 4 }}>
                  <li><strong>Title</strong> (required) — e.g., "SQL Injection in Login Form"</li>
                  <li><strong>Severity</strong> (required) — Critical, High, Medium, Low, or Info</li>
                  <li><strong>CVSS Score</strong> — Use the interactive CVSS Calculator to compute the score and vector</li>
                  <li><strong>Vulnerability Details</strong> — Technical description of the vulnerability</li>
                  <li><strong>Affected Endpoints</strong> — URLs or components affected</li>
                  <li><strong>Observation</strong> — What was observed during testing</li>
                  <li><strong>Steps to Reproduce</strong> — Step-by-step reproduction with inline evidence/screenshots</li>
                  <li><strong>Recommendation</strong> — Specific remediation guidance</li>
                  <li><strong>References</strong> — OWASP, CVE, CWE links</li>
                </ul>
              </li>
              <li>Click <strong>Save Finding</strong></li>
            </ol>

            <div style={s.tip}>
              <strong>AI Assist:</strong> After entering a title (5+ characters), click the <strong>AI Suggest</strong> button. The AI will generate suggested descriptions, recommendations, CVSS scores, and references based on your title. Click <strong>Apply</strong> on any suggestion to populate the field, or dismiss to ignore.
            </div>

            <h3 style={s.h3}>Import from Vulnerability Templates</h3>
            <p style={s.p}>Use pre-built templates to speed up common findings:</p>
            <ol style={s.ol}>
              <li>Click <strong>+ Add Finding</strong>, then search the library in <strong>Import from Template</strong> at the top of the form</li>
              <li>Select a template — it creates a new finding pre-populated with the template's title, severity, details, recommendation, and references</li>
              <li>Edit the finding to customize for this specific engagement</li>
            </ol>

            <h3 style={s.h3}>Import from Burp Suite</h3>
            <ol style={s.ol}>
              <li>In Burp Suite, go to <strong>Target</strong> tab, select issues, right-click → <strong>Report selected issues</strong></li>
              <li>Choose <strong>XML</strong> format and save the file</li>
              <li>In AttackPath, click <strong>Import Burp XML</strong> and upload the file</li>
              <li>Findings are imported with severity mapping and duplicate detection</li>
            </ol>

            <h3 style={s.h3}>Adding Evidence / Screenshots</h3>
            <p style={s.p}>In the finding edit modal, the <strong>Steps to Reproduce</strong> section supports inline evidence:</p>
            <ol style={s.ol}>
              <li>Click the upload area or drag and drop screenshots</li>
              <li>Add captions to each screenshot</li>
              <li>Reorder evidence items as needed</li>
              <li>Evidence appears in the generated report alongside the steps</li>
            </ol>
          </Card>

          {/* Section 5: Workflow */}
          <Card id="workflow" style={{ padding: 24, marginBottom: 24 }}>
            <h2 style={s.h2}>5. Navigating the Workflow</h2>
            <p style={s.p}>Each engagement follows a defined status workflow. Understanding the progression helps keep tests on track.</p>

            <h3 style={s.h3}>Status Progression</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', margin: '12px 0 20px' }}>
              {[
                { status: 'Requested', color: '#6366f1' },
                { status: '→', color: 'var(--text-muted)' },
                { status: 'New', color: '#3b82f6' },
                { status: '→', color: 'var(--text-muted)' },
                { status: 'Scoping', color: '#8b5cf6' },
                { status: '→', color: 'var(--text-muted)' },
                { status: 'Contract Sent', color: '#06b6d4' },
                { status: '→', color: 'var(--text-muted)' },
                { status: 'In Progress', color: '#f59e0b' },
                { status: '→', color: 'var(--text-muted)' },
                { status: 'In Review', color: '#f97316' },
                { status: '→', color: 'var(--text-muted)' },
                { status: 'Complete', color: '#22c55e' },
                { status: '→', color: 'var(--text-muted)' },
                { status: 'In Retest', color: '#14b8a6' },
                { status: '→', color: 'var(--text-muted)' },
                { status: 'Retest Complete', color: '#10b981' },
              ].map((item, i) => item.status === '→' ?
                <span key={i} style={{ color: item.color, fontSize: 14 }}>→</span> :
                <span key={i} style={{ ...s.badge(item.color), fontSize: 12, padding: '3px 10px' }}>{item.status}</span>
              )}
            </div>
            <p style={s.p}>Additional statuses: <span style={s.badge('#6b7280')}>On Hold</span> <span style={s.badge('#ef4444')}>Cancelled</span></p>

            <h3 style={s.h3}>Key Transitions</h3>
            <ul style={s.ul}>
              <li><strong>Requested → New</strong> — Admin accepts the test request and begins scoping</li>
              <li><strong>Scoping → Contract Sent</strong> — Statement of Work sent to the client</li>
              <li><strong>Contract Sent → In Progress</strong> — Testing begins. Assign a tester and set start/end dates</li>
              <li><strong>In Progress → In Review</strong> — Testing complete, report being prepared. Requires start and end dates to be set</li>
              <li><strong>In Review → Complete</strong> — Final report approved. Requires <strong>Technical Manager</strong> or Super Admin sign-off</li>
              <li><strong>Complete → In Retest</strong> — Revalidating the findings this engagement already reported. Only reachable from Complete (or from a previous Retest Complete, for a second round); to run a fresh test of the same target, clone the engagement instead. Entering it records each finding's severity and status as they stand, which is what the revalidation report compares against</li>
              <li><strong>In Retest → Retest Complete</strong> — Freezes the status each finding ended on, so later edits cannot rewrite a delivered report. Requires <strong>Technical Manager</strong> or Super Admin sign-off</li>
              <li><strong>Cancelled</strong> — Requires Technical Manager or Super Admin</li>
            </ul>

            <h3 style={s.h3}>Finding Status Workflow</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', margin: '12px 0 16px' }}>
              <span style={s.badge('#ef4444')}>Open</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>→</span>
              <span style={s.badge('#3b82f6')}>Retest</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>→</span>
              <span style={s.badge('#22c55e')}>Closed</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 11, margin: '0 4px' }}>or</span>
              <span style={s.badge('#f59e0b')}>Reopened</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 11, margin: '0 4px' }}>or</span>
              <span style={s.badge('#6b7280')}>Accepted</span>
            </div>
            <ul style={s.ul}>
              <li><strong>Open</strong> — New finding, not yet addressed</li>
              <li><strong>Retest</strong> — Client submitted remediation, awaiting tester verification</li>
              <li><strong>Closed</strong> — Tester verified the fix is effective</li>
              <li><strong>Reopened</strong> — Tester found the fix was insufficient</li>
              <li><strong>Accepted</strong> — Client formally accepted the risk</li>
            </ul>

            <div style={s.warn}>
              <strong>Remediation Tracking:</strong> Clients can click <strong>Mark as Remediated</strong> on any Open or Reopened finding to submit it for retest with notes. Testers see these in the <strong>Retest</strong> queue and can mark them as <strong>Pass</strong> (Closed) or <strong>Fail</strong> (Reopened) with notes.
            </div>
          </Card>

          {/* Section 6: Reports */}
          <Card id="reports" style={{ padding: 24, marginBottom: 24 }}>
            <h2 style={s.h2}>6. Generating Reports</h2>

            <h3 style={s.h3}>Prerequisites</h3>
            <ul style={s.ul}>
              <li>Engagement must be in <strong>In Review</strong> or <strong>Complete</strong> status</li>
              <li>You must have the <strong>Report Generators</strong> group</li>
              <li>At least one report template must exist</li>
            </ul>

            <h3 style={s.h3}>Generating a Report</h3>
            <ol style={s.ol}>
              <li>Open the engagement detail page</li>
              <li>Click <strong>Generate Report</strong> in the header or Overview tab</li>
              <li>Select a <strong>report template</strong> from the list (the default template is pre-selected)</li>
              <li>Choose the format: <strong>Word (.docx)</strong> or <strong>PDF</strong></li>
              <li>Click <strong>Generate</strong></li>
              <li>The report downloads automatically and appears in the <strong>Reports</strong> tab</li>
            </ol>

            <h3 style={s.h3}>What's in the Report</h3>
            <p style={s.p}>The generated report includes (based on template configuration):</p>
            <ul style={s.ul}>
              <li>Title page with client name, engagement type, dates, and tester</li>
              <li>Table of Contents with page numbers</li>
              <li>Executive Summary</li>
              <li>Finding Dashboard with severity pie chart and counts</li>
              <li>Methodology and Risk Rating sections</li>
              <li>Scope items</li>
              <li>Vulnerability Summary Table</li>
              <li>Detailed Findings (one per page) with evidence/screenshots</li>
              <li>Disclaimer</li>
              <li>Certificate of Authenticity with QR code (MSP-generated reports only)</li>
            </ul>

            <h3 style={s.h3}>After Generation</h3>
            <p style={s.p}>From the <strong>Reports</strong> tab, you can:</p>
            <ul style={s.ul}>
              <li><strong>Download</strong> — Re-download the DOCX or PDF file</li>
              <li><strong>Record Delivery</strong> — Log when and how the report was delivered to the client (email, portal, in-person, secure share)</li>
              <li><strong>Revoke Certificate</strong> — Invalidate the certificate of authenticity (irreversible)</li>
              <li><strong>Delete</strong> — Permanently remove the report (Super Admins only)</li>
            </ul>

            <div style={s.tip}>
              <strong>Certificate of Authenticity:</strong> MSP-generated reports include a QR code on the last page. Clients can scan it to verify the report is authentic at <code style={s.field}>attackpath.org/verify/...</code> — no login required.
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
